import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useAppConfig } from '@/hooks/use-app-config';
import { useSubscriptionStatus, useCreateSubscriptionCheckout } from '@/hooks/use-subscription-status';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCurrencyDetection } from '@/hooks/use-currency-detection';
import { SmartPaywall } from '@/components/subscription/SmartPaywall';

export function FreeTrialBanner() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showTierSelector, setShowTierSelector] = useState(false);
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const { data: appConfig } = useAppConfig();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const createCheckout = useCreateSubscriptionCheckout();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { currency } = useCurrencyDetection();

  useEffect(() => {
    // Hide if user has active subscription
    if (subscriptionStatus?.subscription_status === 'active') {
      setVisible(false);
      return;
    }
    
    // Hide if cancelled but still has time remaining
    if (subscriptionStatus?.subscription_status === 'cancelled' && subscriptionStatus?.current_period_end) {
      const periodEnd = new Date(subscriptionStatus.current_period_end);
      if (periodEnd > new Date()) {
        setVisible(false);
        return;
      }
    }

    if (dismissed) return;

    const handleScroll = () => {
      // Show banner after scrolling down 300px
      if (window.scrollY > 300) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [subscriptionStatus, dismissed]);

  // Don't render if user has active subscription
  if (subscriptionStatus?.subscription_status === 'active') return null;
  
  // Don't render if cancelled but still has time remaining
  if (subscriptionStatus?.subscription_status === 'cancelled' && subscriptionStatus?.current_period_end) {
    const periodEnd = new Date(subscriptionStatus.current_period_end);
    if (periodEnd > new Date()) return null;
  }

  // Don't show if not subscription model
  if (appConfig?.current_model !== 'subscription') return null;

  const handleAcceptTrial = async () => {
    // Check if user is logged in
    if (!user) {
      // Redirect to userflow for signup
      navigate('/userflow');
      return;
    }

    // Show tier selector drawer
    setShowTierSelector(true);
  };

  const handleTierSelected = async (tier: 'monthly' | 'annual', price: number) => {
    setShowTierSelector(false);
    
    try {
      const { url } = await createCheckout(tier, currency.code);
      
      if (url) {
        window.location.href = url;
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start subscription. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setVisible(false);
  };

  // Don't show on mobile - mobile uses the transformed footer menu
  if (isMobile) return null;

  return (
    <>
      <SmartPaywall
        isOpen={showTierSelector}
        onClose={() => setShowTierSelector(false)}
        onPurchaseComplete={() => setShowTierSelector(false)}
      />
      
      <AnimatePresence>
        {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[100] backdrop-blur-xl bg-primary/95 border-t border-primary-foreground/10"
        >
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={handleAcceptTrial}>
              <div className="flex-1 min-w-0" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif' }}>
                <p className="text-primary-foreground text-sm md:text-base font-semibold truncate">
                  {subscriptionStatus?.has_had_subscription 
                    ? 'Welcome back! Renew your subscription to regain access.' 
                    : 'Unlimited access to exclusive stories, new releases added monthly.'}
                </p>
                <p className="text-primary-foreground/80 text-xs md:text-sm mt-0.5">
                  {subscriptionStatus?.has_had_subscription 
                    ? `Just ${currency.symbol}${appConfig?.monthly_price || 7.99}/month to continue.` 
                    : `From ${currency.symbol}${((appConfig?.annual_price || 47.88) / 12).toFixed(2)}/month`}
                </p>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex flex-col items-center gap-1">
                  <Button
                    variant="secondary"
                    size="lg"
                    className="font-semibold whitespace-nowrap bg-white text-black hover:bg-white/90 rounded-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAcceptTrial();
                    }}
                  >
                    {subscriptionStatus?.has_had_subscription ? 'Renew Subscription' : 'Subscribe Now'}
                  </Button>
                  <p className="text-[10px] text-primary-foreground/70" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif' }}>
                    {subscriptionStatus?.has_had_subscription 
                      ? `${currency.symbol}${appConfig?.monthly_price || 7.99}/m. Terms apply` 
                      : `From ${currency.symbol}${((appConfig?.annual_price || 47.88) / 12).toFixed(2)}/m. Terms apply`}
                  </p>
                </div>
                
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss();
                  }}
                  variant="ghost"
                  size="icon"
                  className="text-primary-foreground hover:bg-primary-foreground/10 flex-shrink-0"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
